import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent, Heading } from '@dreryk/react-components';

import { AdditionalInformationTextarea } from '../../shared/AdditionalInformationTextarea';
import { CitoCheckbox } from '../../shared/CitoCheckbox';
import { resolveDiagnosesFormPart } from '../../shared/common.utils';
import { DiagnosesWithBodySideInteractiveTable } from '../../shared/DiagnosesWithBodySideInteractiveTable';
import { StyledReferralForm } from '../../shared/StyledReferralForm';
import { UnitNameAndAddressTextarea } from '../../shared/UnitNameAndAddressTextarea';
import { referralTranslationKeys } from '../../translations';
import { Icd9ProcedureSelector } from '../../shared/Icd9ProcedureField/Icd9ProcedureSelector';

import { HealthcareFacilitySpecialtyField } from './fields/HealthcareFacilitySpecialtyField';
import { ReferralToHospitalFormBean, ReferralToHospitalFormController } from './ReferralToHospitalForm.types';

type ReferralToHospitalFormChildrenProps = Pick<
   ReferralToHospitalFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset' | 'trigger'
>;

export const getInitialValues = (
   referralToHospitalDraft: ReferralToHospitalFormBean | null,
   visitDiagnoses?: VisitDiagnosis[]
): ReferralToHospitalFormBean => {
   return {
      unitNameAndAddress: referralToHospitalDraft?.unitNameAndAddress ?? null,
      cito: referralToHospitalDraft?.cito ?? false,
      healthcareFacilityLocationSpecialty: referralToHospitalDraft?.healthcareFacilityLocationSpecialty ?? null,
      diagnoses: resolveDiagnosesFormPart(referralToHospitalDraft?.diagnoses, visitDiagnoses),
      additionalInformation: referralToHospitalDraft?.additionalInformation ?? null,
      icd9Procedures: referralToHospitalDraft?.icd9Procedures ?? [],
   };
};

type ReferralToHospitalFormProps = {
   readonly referralToHospitalDraft: ReferralToHospitalFormBean | null;
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly visitBeginDate: Date;
   readonly children: (childrenProps: ReferralToHospitalFormChildrenProps) => JSX.Element;
   readonly electronic: boolean;
   readonly visitId: number;
};

export const ReferralToHospitalForm = (props: ReferralToHospitalFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      handleSubmitDraft,
      formState,
      trigger,
      reset,
      watch,
      control,
   } = useFormController<ReferralToHospitalFormBean>(
      getInitialValues(props.referralToHospitalDraft, props.visitDiagnoses)
   );

   return (
      <>
         <Heading
            type={'h1'}
            testId={'referralToHospital'}
            content={
               props.electronic
                  ? referralTranslationKeys.referralType.HOSPITAL_ELECTRONIC
                  : referralTranslationKeys.referralType.HOSPITAL
            }
         />
         <StyledReferralForm onSubmit={(event) => event.preventDefault()} noValidate>
            <FormContent testId={'referralToHospital'}>
               <CitoCheckbox register={register} />
               {!props.electronic && <UnitNameAndAddressTextarea register={register} />}
               <HealthcareFacilitySpecialtyField
                  register={register}
                  watch={watch}
                  visitId={props.visitId}
                  errors={formState.errors}
               />
               <DiagnosesWithBodySideInteractiveTable
                  register={register}
                  visitDiagnoses={props.visitDiagnoses}
                  dictionaryValidOn={props.visitBeginDate}
                  required
               />
               <Icd9ProcedureSelector
                  control={control}
                  dictionaryValidOn={props.visitBeginDate}
                  defaultValues={props.referralToHospitalDraft?.icd9Procedures}
               />
               <AdditionalInformationTextarea register={register} />
               {props.children({
                  handleSubmit: handleSubmit,
                  handleSubmitDraft: handleSubmitDraft,
                  formState: formState,
                  reset: reset,
                  trigger: trigger,
               })}
            </FormContent>
         </StyledReferralForm>
      </>
   );
};
